/// <amd-module name="Repos/PullRequestList/Components/PullRequestsList" />
import * as React from "react";
import { getReposRoutingService } from "Repos/Common/ReposRoutingService";
import { PullRequestSection } from "Repos/PullRequestList/Components/PullRequestSection";
import { IPullRequest, IPullRequestQuery, IPullRequestSection } from "Repos/PullRequestList/Contracts";
import * as Resources from "Repos/PullRequestList/Resources";
import { getTFSProject } from "Tfs/Platform/Page";
import { IReadyableReadonlyObservableArray } from "VSS/Core/Observable";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { onClickFPS } from "VSS/Platform/FPS";
import { VssComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { Link } from "VSSUI/Link";
import { Observer } from "VSSUI/Observer";
import { Spinner } from "VSSUI/Spinner";
import { ITableRow } from "VSSUI/Table";
import { getSafeId } from "VSSUI/Util";
import { IZeroDataItem, ZeroData, ZeroDataActionType } from "VSSUI/ZeroData";

export interface IPullRequestsListProps {
    onLoaded: () => void;
    onToggleDecline: (pullRequest: IPullRequest) => void;
    onToggleFlagged: (pullRequest: IPullRequest) => void;
    sections: IReadyableReadonlyObservableArray<IPullRequestSection>;
    showRepository?: boolean;
    zeroDataMessage?: string;
}

export const PullRequestsList = (props: IPullRequestsListProps) => {
    const { onLoaded, onToggleDecline, onToggleFlagged, sections, showRepository, zeroDataMessage } = props;
    return (
        <Observer sections={sections} allReady={sections.ready}>
            {() => {
                return (
                    <PullRequestsListSections
                        allSectionsReady={sections.ready.value}
                        onLoaded={onLoaded}
                        onToggleDecline={onToggleDecline}
                        onToggleFlagged={onToggleFlagged}
                        sections={sections.value}
                        showRepository={showRepository}
                        zeroDataMessage={zeroDataMessage}
                    />
                );
            }}
        </Observer>
    );
};

interface IPullRequestsListSectionProps {
    allSectionsReady: boolean;
    onLoaded: () => void;
    onToggleDecline: (pullRequest: IPullRequest) => void;
    onToggleFlagged: (pullRequest: IPullRequest) => void;
    sections: IPullRequestSection[];
    showRepository?: boolean;
    zeroDataMessage?: string;
}

class PullRequestsListSections extends VssComponent<IPullRequestsListSectionProps> {
    private loaded = false;

    public render() {
        const { allSectionsReady, sections } = this.props;
        return (
            <>
                {sections.length > 0 && this.renderSections(sections)}
                {!allSectionsReady && <Spinner />}
                {allSectionsReady && sections.length === 0 && this.renderZeroData()}
            </>
        );
    }

    public componentDidMount() {
        super.componentDidMount();
        this.handleLoaded();
    }

    public componentDidUpdate(prevProps: IPullRequestsListSectionProps, prevState: {}) {
        super.componentDidUpdate(prevProps, prevState);
        this.handleLoaded();
    }

    private handleLoaded = () => {
        if (!this.loaded) {
            if (this.props.sections.length || this.props.allSectionsReady) {
                this.loaded = true;
                this.props.onLoaded();
            }
        }
    };

    private renderSections = (sections: IPullRequestSection[]) => {
        const { onToggleDecline, onToggleFlagged, showRepository } = this.props;
        return (
            <>
                {sections.map(section => (
                    <PullRequestSection
                        key={section.query?.id || section.title}
                        onActivateRow={(event: React.SyntheticEvent<HTMLElement>, tableRow: ITableRow<IPullRequest>) => {
                            onClickFPS(this.context.pageContext, tableRow.data.linkProps.href, true, event);
                        }}
                        onCustomizeUserView={
                            section.query
                                ? onDismiss => {
                                      this.showCustomizeUserViewPanel(section.query!, onDismiss);
                                  }
                                : undefined
                        }
                        onToggleDecline={onToggleDecline}
                        onToggleFlagged={onToggleFlagged}
                        section={section}
                        showRepository={showRepository}
                    />
                ))}
            </>
        );
    };

    private renderZeroData = () => {
        const primaryText = this.props.zeroDataMessage || Resources.EmptyPrListingPrimary;

        let actionHref: string | undefined;
        let actionText: string | undefined;
        let onActionClick: ((ev?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, item?: IZeroDataItem) => void) | undefined;

        if (getTFSProject(this.context.pageContext)) {
            actionText = Resources.CreatePullRequest;
            actionHref = getReposRoutingService(this.context.pageContext).getPullRequestCreateUrl();
            onActionClick = event => onClickFPS(this.context.pageContext, actionHref!, true, event);
        }

        return (
            <ZeroData
                imagePath={getAssetLocation(this.context.pageContext, "ms.vss-code-web/pr-list/Content/emptyPRList.svg")}
                imageAltText={primaryText}
                primaryText={primaryText}
                secondaryText={
                    <div id={getSafeId("empty-pr-listing-secondary-text")}>
                        {Resources.EmptyPrListingSecondary}
                        &nbsp;
                        <Link ariaLabel={Resources.EmptyPrListingSecondary} href="https://go.microsoft.com/fwlink/?LinkId=800672" target="_blank">
                            {Resources.EmptyPrListingSecondaryLearnMore}
                        </Link>
                    </div>
                }
                onActionClick={onActionClick}
                actionType={ZeroDataActionType.ctaButton}
                actionText={actionText}
                actionHref={actionHref}
            />
        );
    };

    private showCustomizeUserViewPanel = (query: IPullRequestQuery, onDismiss: () => void) => {
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        contributionService.executeCommandEx(
            "ms.vss-code-web.pr-listing-page-actions-command",
            {
                dependencies: ["ms.vss-code-web.pr-listing-page-actions-content"],
                serviceName: "IPullRequestListingPageActionsService",
                methodName: "showCustomizePanel"
            },
            query,
            onDismiss
        );
    };
}
